import React, { Fragment, useContext, useState } from "react";
import { FiBox, FiChevronDown, FiChevronUp, FiCreditCard, FiEdit, FiShoppingBag } from "react-icons/fi";
import { GridLegacy as Grid, Button, useMediaQuery, useTheme } from "@mui/material";

import {
  api,
  Booking,
  IBookingSalesAndPaymentsResult,
  Package,
  PaymentTypes,
  ProductTypes,
} from "../../../logic/api";

import styles from "../styles";
import classes from "./BookingProducts.module.css";
import flex from "../../../styles/flex";

import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import PaymentDialogV2 from "./components/paymentDialog/PaymentDialogV2";
import EditProductsDialog from "./components/EditProductsDialog";
import DocumentGenerator from "../BookingView/components/DocumentGenerator";
import PackagePicker from "../../../components/pickers/packagePicker/PackagePickerDialog";
import ConfirmationDialog from "../../../components/notifications/confirmationDialog/ConfirmationDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import SaleItemSource from "./components/SaleItemSource";

import { SessionContext } from "../../../utility/useSession";
import { usePreferences } from "../../../utility/usePreferences";
import { useDate } from "../../../utility/useDate";
import {
  formatCurrency,
  formatPercentage,
  paymentTypeLabelMap,
  getDisplayTaxRate,
  getDisplayTaxValue,
} from "../../../utility/utils";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import AlertDialog from "../../../components/notifications/alertDialog/AlertDialog";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";

const BookingProducts: React.FunctionComponent<{
  showPaymentDialog: boolean;
  openPaymentDialog: () => void;
  onPaymentDialogClose?: (updated: boolean) => void;
  booking: Booking;
  saleData: IBookingSalesAndPaymentsResult;
  readOnly: boolean;
  booking_products_enabled: boolean;
  booking_payments_enabled: boolean;
  booking_packages_enabled: boolean;
  onReloadRequest: () => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [showEditProducts, setShowEditProducts] = useState(false);
  const [showSwitchPackage, setShowSwitchPackage] = useState(false);
  const [switchPackaged, setSwitchPackage] = useState<Package | null>(null);
  const [
    showPackageSwitchConfirmation,
    setShowPackageSwitchConfirmation,
  ] = useState(false);
  const [showVoidAlert, setShowVoidAlert] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const { usesPaymentMethod } = usePreferences();
  const { toFormattedDateTimeString, localizeDate } = useDate();
  const isAdditionalTaxRatesReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalTaxRates);
  const deLinkTaxNameAndTaxNumber = useReleaseManagement(ReleaseFlag.releaseDeLinkCustomerTaxNameAndTaxNumber);
  const salesReceiptTemplateChanges = useReleaseManagement(ReleaseFlag.releaseSalesReceiptTemplateChanges);
  const isPreBookedPackagePriceReleased = useReleaseManagement(ReleaseFlag.releasePreBookedPackagePrice);
  const isInvoiceStayDurationReleased = useReleaseManagement(ReleaseFlag.releaseInvoiceStayDuration);

  const switchPackageRequest = api.hooks.packages.switchPackage();

  const buildTaxStackItems = () => {
    if (!salesReceiptTemplateChanges) return []

    const prefs = session.current!.site!.preferences
    if (deLinkTaxNameAndTaxNumber) {
      if (prefs.company_taxName?.length) {
        return [{
          text: `${prefs.company_taxName}: ${prefs.company_vatno}`,
          alignment: "right" as const,
          style: "address" as const,
        }]
      } else {
        return [{
          text: `${prefs.vat_name} Number: ${prefs.company_vatno}`,
          alignment: "right" as const,
          style: "address" as const,
        }]
      }
    } else {
      if (prefs.company_vatno?.length) {
        return [{
          text: `${prefs.vat_name} Number: ${prefs.company_vatno}`,
          alignment: "right" as const,
          style: "address" as const,
        }]
      }
      return []
    }
  }


  if (session.current.site == null) {
    return <Fragment />;
  }

  const isLargeDevice = useMediaQuery(theme.breakpoints.up("md"));
  const reconciliations = props.saleData.reconciliations;
  const payments = props.saleData.payments || [];
  let saleItems = props.saleData.saleItems || [];

  //payments total where paymentType is not complimentary or refund
  const paymentsTotal = props.saleData.payments
    .filter(payment => ![PaymentTypes.compDiscount, PaymentTypes.refund].includes(payment.paymentType))
    .reduce((total, payment) => total + parseFloat(Number(payment.amount).toFixed(2)) * 100, 0) / 100;

  //payments total where paymentType is complimentary
  const complimentaryTotal = props.saleData.payments
    .filter(payment => [PaymentTypes.compDiscount].includes(payment.paymentType))
    .reduce((total, payment) => total + parseFloat(Number(payment.amount).toFixed(2)) * 100, 0) / 100;

  if (expanded == false) {
    saleItems = saleItems.filter((p) => {
      return p.voided != true;
    });
  }

  const paymentsTable =
    //#region Reconciliations Table
    <>
      {<div style={{ borderTop: "solid 1px #eee", padding: 5 }}>
        <div style={flex.flexRows}>
          <div style={{ ...flex.flexAuto, width: 150, textAlign: "left" }}><b>{t("input.labels.paymentDate")}</b></div>
          <div style={{ ...flex.flexAuto, width: 200, textAlign: "left" }}><b>{t("input.labels.productName")}</b></div>
          <div style={{ ...flex.flexAuto, width: 180, textAlign: "left" }}><b>{t("input.labels.method")}</b></div>
          <div style={{ ...flex.flexAuto, width: 180, textAlign: "left" }}><b>{t("input.labels.authCode")}</b></div>
          <div style={flex.flexFill}><b>{t("general.description")}</b></div>
          <div style={{ ...flex.flexFill, width: 100, textAlign: "right" }}><b>{t("input.labels.amount")}</b></div>
        </div>
      </div>}
      {reconciliations && reconciliations.length > 0 ? reconciliations.filter(reconciliation => reconciliation.amount !== null)
        .map((reconciliation, key) => {

          return (
            <div
              style={{ borderTop: "solid 1px #eee", padding: 5 }}
              key={key}
            >
              <div style={flex.flexRows}>
                <div style={flex.flexAuto}>
                  <div style={{ width: 150, textAlign: "left" }}>
                    {toFormattedDateTimeString(new Date(reconciliation.createdAt))}
                  </div>
                </div>
                <div style={{ ...flex.flexAuto, textAlign: "left", width: 200 }}>
                  {reconciliation.productSaleItem.name}
                </div>
                <div style={flex.flexAuto}>
                  <div style={{ width: 180, textAlign: "left" }}>
                    <small>
                      <b>
                        {paymentTypeLabelMap.get(reconciliation.payment.paymentType)}
                      </b>
                    </small>
                  </div>
                </div>
                <div style={flex.flexAuto}>
                  <div style={{ width: 180, textAlign: "left" }}>
                    <small>
                      <b>
                        {[PaymentTypes.creditCard, PaymentTypes.debitCard, PaymentTypes.creditDebitCard].includes(reconciliation.payment.paymentType)
                          ? (
                            <b>
                              {!reconciliation.payment.authCode
                                ? "-"
                                : `${reconciliation.payment.authCode} `}
                            </b>
                          ) : (
                            "-"
                          )}
                      </b>
                    </small>
                  </div>
                </div>
                <div style={flex.flexFill}>
                  {!reconciliation.payment.description
                    ? "-"
                    : reconciliation.payment.description}
                </div>
                <div style={{
                  ...flex.flexFill, width: 100, textAlign: "right", color:
                    reconciliation.payment.paymentType === PaymentTypes.refund
                      ? "orange"
                      : "green",
                }
                }>
                  <b>{formatCurrency(reconciliation.amount, session)}</b>
                </div>
              </div>
            </div>
          );
        })
        : <div style={{ ...flex.flexFill, borderTop: "solid 1px #eee", padding: 5, textAlign: "center" }}>{t("errors.noPayments")}</div>}
    </>
  //#endregion

  const productsTable =
    //#region Reconciliation Products Table
    <>
      <Table className={classes.dataTable}>
        <TableHead>
          <TableRow>
            <TableCell>{t("input.labels.saleDate")}</TableCell>
            <TableCell>{t("input.labels.productName")}</TableCell>
            <TableCell>{t("payments.rate")}</TableCell>
            <TableCell className={classes.numeric}>{t("input.labels.amount")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {saleItems.map((item) => {
            const cost = formatCurrency((item.rate * item.qty), session);
            const rate = formatCurrency((item.rate), session);
            const isUpsell = item.isUpsell === true;

            return (
              <TableRow
                className={item.productType === ProductTypes.overstay ? classes.warning : ""}
                key={item.id}
              >
                <TableCell>
                  {item.prePaid ? " " : toFormattedDateTimeString(new Date(item.saleDate))}
                </TableCell>
                <TableCell>
                  {item.voided && (
                    <Fragment>
                      <s>{item.name}</s>
                      {" "}
                      <Trans i18nKey="booking.labels.voidAt">
                        VOID at {{ date: toFormattedDateTimeString(new Date(item.voidDate)) }}
                      </Trans>
                      {item.voidBy != null && (
                        <Fragment>
                          &nbsp;by {item.voidBy.firstName}{" "}
                          {item.voidBy.lastName}
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                  {item.voided != true && <>{item.name}</>}
                </TableCell>
                <TableCell>
                  <div style={{ width: 150, textAlign: "left" }}>
                    {item.prePaid === true ? (
                      <small>
                        <b>x{item.qty}</b>
                      </small>
                    ) : (
                      <Fragment>
                        {item.voided === true && (
                          <s>
                            <small>
                              <b>x{item.qty}</b> @ {rate}
                            </small>
                          </s>
                        )}
                        {item.voided !== true && (
                          <small>
                            <b>x{item.qty}</b> @ {rate}
                          </small>
                        )}
                      </Fragment>
                    )}
                  </div>
                </TableCell>
                {!isAdditionalTaxRatesReleased && <TableCell>
                  <div style={{ width: 150, textAlign: "left" }}>
                    {item.prePaid ? (
                      <small>-</small>
                    ) : (
                      <Fragment>
                        {item.voided && (
                          <s>
                            <small>
                              <b>Inc Tax @ {formatPercentage(item.vat)}</b>
                            </small>
                          </s>
                        )}
                        {!item.voided && (
                          <small>
                            <b>Inc Tax @  {formatPercentage(item.vat)}</b>
                          </small>
                        )}
                      </Fragment>
                    )}
                  </div>
                </TableCell>}
                <TableCell className={classes.numeric}>
                  <div style={flex.flexRows}>
                    <div style={{ marginRight: 5 }}>
                      {!item.prePaid && <SaleItemSource source={item.source} />}
                    </div>
                    <div style={{ marginRight: 5, width: 20 }}>
                      {isUpsell && (
                        <FiShoppingBag color="blue" title={t("upsellPackage", { packageName: item.upsellPackageName ?? "", packageCode: item.upsellPackageCode })} />

                      )}
                    </div>
                    {item.voided && (
                      <div style={{ width: 50, paddingRight: 10 }}>
                        <div
                          style={{
                            backgroundColor: "purple",
                            paddingRight: 5,
                            paddingLeft: 5,
                            borderRadius: 3,
                          }}
                        >
                          <b style={{ color: "white" }}>{t("booking.labels.void")}</b>
                        </div>
                      </div>
                    )}
                    <div style={flex.flexFill}>
                      {!item.prePaid &&
                        !item.voided && <Fragment><b>{cost}</b></Fragment>}
                      {!item.prePaid && item.voided && (
                        <s>
                          <b>{cost}</b>
                        </s>
                      )}
                      {item.prePaid && (
                        <Fragment>
                          <small>
                            <b style={{ color: "green" }}>
                              {t("booking.labels.preBooked")}
                            </b>
                          </small>
                          {isPreBookedPackagePriceReleased && (
                            <b style={{ marginLeft: 4 }}>
                              {formatCurrency(item.rate, session)}
                            </b>
                          )}
                        </Fragment>
                      )}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  //#endregion

  const summary =
    //#region Reconciliation Summary
    <div style={{ paddingLeft: 20, paddingTop: 20, paddingRight: 5 }}>
      {expanded && (
        <Fragment>
          {props.saleData.voidTotal > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={flex.flexRows}>
                <div
                  style={{
                    ...flex.flexFill,
                    textAlign: "right",
                  }}
                >
                  {t("booking.labels.void")}
                </div>
                <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                  <b style={{ color: "purple" }}>
                    {formatCurrency(props.saleData.voidTotal, session)}
                  </b>
                </div>
              </div>
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <div style={flex.flexRows}>
              <div
                style={{
                  ...flex.flexFill,
                  width: 120,
                  textAlign: "right",
                }}
              >
                Subtotal<small> (Ex Tax)</small>
              </div>
              <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                <b>
                  {formatCurrency(props.saleData.subTotal, session)}
                </b>

              </div>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={flex.flexRows}>
              <div
                style={{
                  ...flex.flexFill,
                  width: 100,
                  textAlign: "right",
                }}
              >
                {isAdditionalTaxRatesReleased ? session.current.site.preferences.vat_name : 'Tax'}
              </div>
              <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                <b>
                  {formatCurrency(props.saleData.vatAmount, session)}
                </b>

              </div>
            </div>
          </div>

          {isAdditionalTaxRatesReleased && session.current.site.preferences.vat_name_2 && session.current.site.preferences.vat_rate_2 && <div style={{ marginTop: 10 }}>
            <div style={flex.flexRows}>
              <div
                style={{
                  ...flex.flexFill,
                  width: 100,
                  textAlign: "right",
                }}
              >
                {session.current.site.preferences.vat_name_2}
              </div>
              <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                <b>
                  {props.saleData.vat2Amount && formatCurrency(props.saleData.vat2Amount, session)}
                </b>
              </div>
            </div>
          </div>}

          <div style={{ marginTop: 10 }}>
            <div style={flex.flexRows}>
              <div
                style={{
                  ...flex.flexFill,
                  width: 100,
                  textAlign: "right",
                }}
              >
                Total
              </div>
              <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                <b>
                  {formatCurrency(props.saleData.total, session)}
                </b>

              </div>
            </div>
          </div>


          <div style={{ marginTop: 10 }}>
            <div style={flex.flexRows}>
              <div
                style={{
                  ...flex.flexFill,
                  width: 100,
                  textAlign: "right",
                }}
              >
                Refunds
              </div>
              <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                <b style={{ color: "orange" }}>

                  {formatCurrency(props.saleData.refundsTotal, session)}
                </b>
              </div>
            </div>
          </div>


          <div style={{ marginTop: 10 }}>
            <div style={flex.flexRows}>
              <div
                style={{
                  ...flex.flexFill,
                  width: 100,
                  textAlign: "right",
                }}
              >
                Payments
              </div>
              <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
                <b style={{ color: "green" }}>
                  {formatCurrency(props.saleData.paymentsTotal, session)}
                </b>
              </div>
            </div>
          </div>

        </Fragment>
      )}

      <div style={{ marginTop: 10 }}>
        <div style={flex.flexRows}>
          <div
            style={{
              ...flex.flexFill,
              width: 100,
              textAlign: "right",
            }}
          >
            {props.saleData.outstanding < 0 ? (
              <b style={{ color: "red" }}>OVERPAYMENT</b>
            ) : (
              <Fragment>Outstanding</Fragment>
            )}
          </div>
          <div style={{ ...flex.flexAuto, width: isLargeDevice ? 100 : "50%", textAlign: "right" }}>
            <div>
              <b
                style={{
                  fontSize: 20,
                  color:
                    props.saleData.outstanding > 0 || props.saleData.outstanding < 0 ? "red" : "black",
                }}
              >
                {formatCurrency(props.saleData.outstanding, session)}
              </b>
            </div>
            {props.saleData.outstanding < 0 && (
              <div>
                <b>The customer has overpaid</b>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  //#endregion

  const ProductManagerButtons = () => <Fragment>
    {props.booking_products_enabled == true && (
      <div style={flex.flexAuto}>
        <div style={{ paddingRight: 10 }}>
          <Button
            onClick={() => {
              setShowEditProducts(true);
            }}
            variant="contained"
            color="secondary"
          >
            <FiEdit size={23} style={{ marginRight: 5 }}></FiEdit> {isLargeDevice && <Fragment>Products</Fragment>}
          </Button>
        </div>
      </div>
    )}
    {props.booking_packages_enabled == true && (
      <div style={flex.flexAuto}>
        <div style={{ paddingRight: 10 }}>
          <Button
            onClick={() => {
              setShowSwitchPackage(true);
            }}
            variant="contained">
            <FiBox size={23} style={{ marginRight: 5 }}></FiBox> {isLargeDevice && <Fragment>Package</Fragment>}
          </Button>
        </div>
      </div>

    )}
    {props.booking_payments_enabled == true && (
      <div style={flex.flexAuto}>
        <div style={{ paddingRight: 10 }}>
          <Button
            variant="contained"
            onClick={() => {
              props.openPaymentDialog();
            }}>
            <FiCreditCard size={23} style={{ marginRight: 5 }}></FiCreditCard> {isLargeDevice && <Fragment>Payments</Fragment>}
          </Button>
        </div>
      </div>

    )}

    {props.booking_products_enabled == true && (
      <div style={flex.flexAuto}>
        <DocumentGenerator
          onRequestSerial={() => {
            if (session.current.site == null) {
              return "";
            }
            let currentSite = "";

            if (session.current.site.home != null) {
              if (session.current.credential != null) {
                currentSite = session.current.credential.database;
              }
              currentSite = `${currentSite} - ${session.current.site.home.name}`;
            }

            let saleFields = [
              "Date",
              "Item",
              "Rate",
              "Quantity",
              "Amount",
              session.current.site.preferences.vat_name,
              `${session.current.site.preferences.vat_name} Rate`,
              "Line Total",
            ];

            if (isAdditionalTaxRatesReleased && session.current.site.preferences.vat_name_2 && session.current.site.preferences.vat_rate_2) {
              saleFields = [
                "Date",
                "Item",
                "Rate",
                "Quantity",
                "Amount",
                session.current.site.preferences.vat_name,
                `${session.current.site.preferences.vat_name} Rate`,
                session.current.site.preferences.vat_name_2,
                `${session.current.site.preferences.vat_name_2} Rate`,
                "Line Total",
              ];
            }

            const saleRecords = [];

            for (const si of saleItems) {
              if (si.voided != true) {
                const rateCalculated = si.rateCalculated == null ? 0 : si.rateCalculated;
                const vatCalculated = si.vatCalculated == null ? 0 : si.vatCalculated;
                const vat2Calculated = si.vat2Calculated ?? 0;
                const subCalculated = si.subCalculated == null ? 0 : si.subCalculated;
                const totalCalculated = si.totalCalculated == null ? 0 : si.totalCalculated;

                const legacySalesItemRow = {
                  Date: si.prePaid == true
                    ? " "
                    : toFormattedDateTimeString(new Date(si.saleDate)),
                  Item: si.name,
                  Rate:
                    si.prePaid == true
                      ? "PRE-PAID"
                      : formatCurrency(rateCalculated, session),
                  Quantity: si.qty,
                  Amount: si.prePaid == true
                    ? "-"
                    : formatCurrency(subCalculated, session),
                  "Line Total":
                    si.prePaid == true
                      ? formatCurrency(0, session)
                      : formatCurrency(totalCalculated, session),
                }

                const newSalesItemRow = {
                  Date: si.prePaid == true
                    ? " "
                    : toFormattedDateTimeString(new Date(si.saleDate)),
                  Item: si.name,
                  Rate: si.prePaid == true
                    ? "PRE-PAID"
                    : formatCurrency(rateCalculated, session),
                  Quantity: si.qty,
                  Amount: formatCurrency(subCalculated, session),
                  "Line Total": formatCurrency(totalCalculated, session)
                }

                const saleRecord: Record<string, unknown> = isPreBookedPackagePriceReleased
                  ? newSalesItemRow
                  : legacySalesItemRow;

                saleRecord[session.current.site.preferences.vat_name] = getDisplayTaxValue(isPreBookedPackagePriceReleased, si.prePaid, vatCalculated, session);
                saleRecord[`${session.current.site.preferences.vat_name} Rate`] = getDisplayTaxRate(isPreBookedPackagePriceReleased, si.prePaid, si.vat);

                if (isAdditionalTaxRatesReleased && session.current.site.preferences.vat_name_2) {
                  saleRecord[session.current.site.preferences.vat_name_2] = getDisplayTaxValue(isPreBookedPackagePriceReleased, si.prePaid, vat2Calculated, session);
                  saleRecord[`${session.current.site.preferences.vat_name_2} Rate`] = getDisplayTaxRate(isPreBookedPackagePriceReleased, si.prePaid, si.vat2);
                }

                saleRecords.push(saleRecord);
              }
            }


            const paymentFields = [
              t("input.labels.date"),
              t("input.labels.method"),
              t("input.labels.authCode"),
              t("general.description"),
              t("input.labels.amount"),
            ];

            if (usesPaymentMethod(PaymentTypes.account)) {
              paymentFields.splice(3, 0, t("input.labels.accountNumber"))
            }

            const paymentRecords = [];

            const paymentRecordTranslations = {
              "Auth Code": t("input.labels.authCode")
            }

            for (const p of payments) {
              const paymentRecord: Record<string, string | number | null> = {
                ...paymentRecordTranslations,
                Date: toFormattedDateTimeString(new Date(p.paymentDate)),
                Method: paymentTypeLabelMap.get(p.paymentType) || t("general.unknown"),
                "Auth Code": p.authCode,
                Description: p.description,
                Amount: formatCurrency(p.amount, session),
              };

              if (usesPaymentMethod(PaymentTypes.account)) {
                paymentRecord[t("input.labels.accountNumber")] = p.accountNumber;
              }

              paymentRecords.push(paymentRecord);
            }

            const leftStack = [];

            if (
              session.current.site.preferences.company_logo_base64 !=
              null
            ) {
              if (
                session.current.site.preferences.company_logo_base64
                  .length > 0
              ) {
                leftStack.push({
                  image:
                    `data:image/png;base64,${session.current.site.preferences.company_logo_base64}`,
                  width: 150,
                });
                leftStack.push({
                  text: " ",
                  alignment: "left",
                  style: "address",
                });
              }
            }

            const dateText = toFormattedDateTimeString(new Date())
            if (!salesReceiptTemplateChanges) {
              leftStack.push({
                text: `Sales Receipt - ${dateText}`,
                style: "header",
                lineHeight: 1.5,
              })
            } else {
              leftStack.push({
                text: `Tax Invoice ${isInvoiceStayDurationReleased ? '' : ` - ${dateText}`}`,
                style: "header",
                lineHeight: 1.5,
              })
            }
            leftStack.push({ text: " ", alignment: "left", style: "address" })

            leftStack.push({
              text: " ",
              alignment: "left",
              style: "address",
            });

            leftStack.push({
              text: `Booking:        ${props.booking.ref}`,
              alignment: "left",
              style: "address",
              lineHeight: 1.2,
            });

            if (!salesReceiptTemplateChanges) {
              if (deLinkTaxNameAndTaxNumber) {
                if (session.current.site.preferences.company_taxName?.length > 0) {
                  leftStack.push({
                    text: `${session.current.site.preferences.company_taxName}: ${session.current.site.preferences.company_vatno}`,
                    alignment: "left",
                    style: "address",
                  });
                } else {
                  leftStack.push({
                    text: `${session.current.site.preferences.vat_name} Number: ${session.current.site.preferences.company_vatno}`,
                    alignment: "left",
                    style: "address",
                  });
                }
              } else if (session.current.site.preferences.company_vatno?.length > 0) {
                leftStack.push({
                  text: `${session.current.site.preferences.vat_name} Number: ${session.current.site.preferences.company_vatno}`,
                  alignment: "left",
                  style: "address",
                });
              }
            }

            if (salesReceiptTemplateChanges) {
              leftStack.push({
                text: `${t("booking.cards.customer")}:     ${props.booking.leadContact.fullName}`,
                alignment: "left",
                style: "address",
              });
            }
            if (isInvoiceStayDurationReleased) {
              if (props.booking.vehicle.checkInDate) {
                leftStack.push({
                  text: `${t("input.labels.entryDate")}:    ${toFormattedDateTimeString(new Date(props.booking.vehicle.checkInDate))}`,
                  alignment: "left",
                  style: "address",
                });
              }
              if (props.booking.vehicle.checkOutDate) {
                leftStack.push({
                  text: `${t("input.labels.exitDate")}:       ${toFormattedDateTimeString(new Date(props.booking.vehicle.checkOutDate))}`,
                  alignment: "left",
                  style: "address",
                });
              }
            }
            const rightStack = [];
            const prefs = session.current.site.preferences

            rightStack.push(
              { text: prefs.company_name, alignment: "right", style: "header" },
              { text: currentSite, alignment: "right", style: "address" },
              { text: " ", alignment: "right", style: "address" },
              { text: prefs.company_website, alignment: "right", style: "address" },
              { text: prefs.company_email, alignment: "right", style: "address" },
              { text: prefs.company_telephone, alignment: "right", style: "address" },
              ...(prefs.company_registration
                ? [{
                  text: `${t("settings.companyReg")}: ${prefs.company_registration}`,
                  alignment: "right",
                  style: "address",
                }]
                : []),
            )
            rightStack.push(...buildTaxStackItems())

            rightStack.push(
              { text: " ", alignment: "right", style: "address" },
              { text: prefs.company_address_line1, alignment: "right", style: "address" },
              { text: prefs.company_address_line2, alignment: "right", style: "address" },
              { text: prefs.company_address_line3, alignment: "right", style: "address" },
              { text: prefs.company_address_county, alignment: "right", style: "address" },
              { text: prefs.company_address_postcode, alignment: "right", style: "address" },
              { text: prefs.company_address_country, alignment: "right", style: "address" },
            )

            const doc = {
              pageSize: "A4",
              pageOrientation: "portrait",
              pageMargins: [20, 20, 20, 20],

              content: [
                {
                  columns: [
                    {
                      width: "*",
                      stack: leftStack,
                    },
                    {
                      width: "auto",
                      stack: rightStack
                    },
                  ],
                },
                { text: " ", style: "address", lineHeight: 1.5 },
                { text: t("booking.labels.products"), style: "subheader", lineHeight: 2 },

                {
                  layout: "lightHorizontalLines",
                  table: {
                    headerRows: 1,
                    widths: saleFields.map((field) => {
                      if (field == "Item") {
                        return "*";
                      }
                      return "auto";
                    }),

                    body: [
                      saleFields.map((field) => {
                        return { text: field, style: "tablehead" };
                      }),
                      ...saleRecords.map((record) => {
                        const rec: Record<string, unknown> = record;
                        return saleFields.map((field) => {
                          let value = "";
                          value = ((rec[field] == null
                            ? ""
                            : rec[field]) as string).toString();
                          return { text: value, style: "tablecell" };
                        });
                      }),
                    ],
                  },
                },
                { text: " ", style: "header", lineHeight: 2 },
                { text: "Payments", style: "subheader", lineHeight: 2 },
                {
                  layout: "lightHorizontalLines", // optional
                  table: {
                    headerRows: 1,
                    widths: paymentFields.map((field) => {
                      if (field == t("general.description")) {
                        return "*";
                      }

                      if (field === t("input.labels.accountNumber")) {
                        return 96;
                      }

                      return "auto";
                    }),
                    body: [
                      paymentFields.map((field) => {
                        return { text: field, style: "tablehead" };
                      }),
                      ...paymentRecords.map((record) => {
                        const rec: Record<string, unknown> = record;
                        return paymentFields.map((field) => {
                          let value = "";
                          value = ((rec[field] == null
                            ? ""
                            : rec[field]) as string).toString();
                          return { text: value, style: "tablecell" };
                        });
                      }),
                    ],
                  },
                },
                { text: " ", style: "header", lineHeight: 2 },
                {
                  layout: {
                    defaultBorder: false,
                  },
                  table: {
                    headerRows: 1,
                    widths: ["*", 150, 80],
                    body: [
                      [
                        { text: "" },
                        {
                          text: isAdditionalTaxRatesReleased ? t("payments.subtotalExTax") :
                            t("payments.subtotalExVAT", { VAT: session.current.site.preferences.vat_name }),
                          alignment: "right",
                          style: "invoiceline",
                        },
                        {
                          text: formatCurrency(props.saleData.subTotal, session),
                          style: "invoiceline",
                        },
                      ],
                      [
                        { text: "" },
                        {
                          text: session.current.site.preferences.vat_name,
                          alignment: "right",
                          style: "invoiceline",
                        },
                        {
                          text: formatCurrency(props.saleData.vatAmount, session),
                          style: "invoiceline",
                        },
                      ],
                      [
                        { text: "" },
                        {
                          text: t("payments.total"),
                          alignment: "right",
                          style: "invoiceline",
                        },
                        {
                          text: formatCurrency(props.saleData.paymentsTotal, session),
                          style: "invoiceline",
                        },
                      ],



                      [
                        { text: "" },
                        {
                          text: t("payments.refunds"),
                          alignment: "right",
                          style: "invoiceline",
                        },
                        {
                          text: formatCurrency(props.saleData.refundsTotal, session),
                          style: "invoiceline",
                        },
                      ],
                      [
                        { text: "" },
                        {
                          text: t("payments.complimentary"),
                          alignment: "right",
                          style: "invoiceline",
                        },
                        {
                          text: formatCurrency(complimentaryTotal, session),
                          style: "invoiceline",
                        },
                      ],
                      [
                        { text: "" },
                        {
                          text: t("payments.payments"),
                          alignment: "right",
                          style: "invoiceline",
                        },
                        {
                          text: formatCurrency(paymentsTotal, session),
                          style: "invoiceline",
                        },
                      ],

                      [{ text: "" }, { text: "" }, { text: "" }],
                      [
                        { text: "" },
                        {
                          text: t("payments.outstanding"),
                          style: "invoicelinebold",
                          alignment: "right",
                        },
                        {
                          text: formatCurrency(props.saleData.outstanding, session),

                          style: "invoicelinebold",
                        },
                      ],
                    ],
                  },
                },
              ],
              styles: {
                header: {
                  fontSize: 16,
                  bold: true,
                },
                subheader: {
                  fontSize: 14,
                  bold: true,
                },
                footer: {
                  fontSize: 10,
                  bold: true,
                },
                recordcount: {
                  fontSize: 16,
                  bold: true,
                },
                timestamp: {
                  fontSize: 14,
                  bold: false,
                },
                tablehead: {
                  fontSize: 10,
                  bold: true,
                },
                tablecell: {
                  fontSize: 10,
                },
                invoiceline: {
                  fontSize: 10,
                },
                invoicelinebold: {
                  fontSize: 14,
                  bold: true,
                },
                address: {
                  fontSize: 10,
                },
              },
            };

            if (isAdditionalTaxRatesReleased && props.saleData.vat2Amount && session.current.site.preferences.vat_name_2) {
              const vat2Content = [
                { text: "" },
                {
                  text: session.current.site.preferences.vat_name_2,
                  alignment: "right",
                  style: "invoiceline",
                },
                {
                  text: formatCurrency(props.saleData.vat2Amount, session),
                  style: "invoiceline",
                }
              ]
              doc.content[doc.content.length - 1].table?.body.splice(2, 0, vat2Content)
            }

            return JSON.stringify(doc);
          }}
        />
      </div>
    )}

  </Fragment>;

  return (
    <Fragment>
      {reconciliations ? <EditProductsDialog
        saleItems={saleItems}
        reconciliations={reconciliations}
        show={showEditProducts}
        bookingId={props.booking.id}
        returnDate={props.booking.expectedReturnDate}
        onClose={() => {
          setShowEditProducts(false);
        }}
        onRequestUpdate={() => {
          setExpanded(true);
          props.onReloadRequest();
        }}
      ></EditProductsDialog> : <EditProductsDialog
        saleItems={saleItems}
        show={showEditProducts}
        bookingId={props.booking.id}
        returnDate={props.booking.expectedReturnDate}
        onClose={() => {
          setShowEditProducts(false);
        }}
        onRequestUpdate={() => {
          setExpanded(true);
          props.onReloadRequest();
        }}
      ></EditProductsDialog>}
      <ConfirmationDialog
        title={t("confirmationDialog.switchPackage.title")}
        message={t("confirmationDialog.switchPackage.message")}
        show={showPackageSwitchConfirmation}
        onClose={(result) => {
          const pkg = switchPackaged;
          setSwitchPackage(null);
          setShowPackageSwitchConfirmation(false);
          if (pkg != null) {
            if (result == true) {
              switchPackageRequest.send(
                session.current.context,
                {
                  bookingId: props.booking.id,
                  packageCode: pkg.code,
                  saleDate: localizeDate(new Date()).toISO() ?? "",
                },
                (result) => {
                  if (result != null) {
                    props.onReloadRequest();
                  }
                }
              );
            }
          }
        }}
      ></ConfirmationDialog>
      <AlertDialog
        title={t("alertDialog.cannotVoidPackage.title")}
        message={t("alertDialog.cannotVoidPackage.message")}
        show={showVoidAlert}
        onClose={() => {
          setShowVoidAlert(false);
        }}
      ></AlertDialog>
      <ErrorDialog err={switchPackageRequest.err}></ErrorDialog>
      <ProgressDialog status={switchPackageRequest.status}></ProgressDialog>
      <PackagePicker
        currentPackageCode={props.booking.packageCode}
        title={t("input.labels.changePackage")}
        show={showSwitchPackage}
        onClose={(newPackage) => {
          if (newPackage) {
            let hasPackageBeenPaidFor = false;

            if (props.saleData.reconciliations) {
              //get all packages against the booking
              const packageSaleItems = props.saleData.saleItems.filter(saleItem => saleItem.packageCode);

              for (const packageItem of packageSaleItems) {
                //find all reconciliations against that package
                const saleItemReconciliations = props.saleData.reconciliations?.filter(reconciliation => reconciliation.productSaleItem.packageCode === packageItem.packageCode);
                //total amount paid on that package and if there is anything outstanding
                const saleItemTotalPayments = saleItemReconciliations?.reduce((total, sir) => total += Number(sir.amount), 0);

                if (saleItemTotalPayments > 0) {
                  hasPackageBeenPaidFor = true;
                }
              }
            }

            if (hasPackageBeenPaidFor) {
              setShowVoidAlert(true);
            }
            else {
              setSwitchPackage(newPackage);
              setShowPackageSwitchConfirmation(true);
            }
          }
          setShowSwitchPackage(false);
        }}
        booking={props.booking}
      ></PackagePicker>
      {props.showPaymentDialog && <PaymentDialogV2
        saleData={props.saleData}
        booking={props.booking}
        show={props.showPaymentDialog}
        onClose={(updated) => {
          props.onPaymentDialogClose && props.onPaymentDialogClose(updated);

          if (updated == true) {
            setExpanded(true);
          }
        }} />
      }

      <Grid item xs={12}>
        <NotchedCard
          style={styles.card}
          label={t("booking.cards.addons")}
          center={true}
        >


          <div style={{ paddingBottom: 10 }}>
            <div style={isLargeDevice ? flex.flexRows : {}}>

              {(props.readOnly == false && isLargeDevice == false) && (
                <div style={{ ...flex.flexRows, marginBottom: 20 }}>
                  <ProductManagerButtons />
                </div>
              )}

              {props.booking_products_enabled == true && (
                <div style={flex.flexAuto}>
                  <div style={{ paddingRight: 10 }}>
                    <Button
                      onClick={() => {
                        setExpanded(!expanded);
                      }}
                    >
                      {expanded == false && (
                        <Fragment>
                          <FiChevronDown
                            style={{ marginRight: 5 }}
                          ></FiChevronDown>
                          {t("general.showMore")}
                        </Fragment>
                      )}
                      {expanded == true && (
                        <Fragment>
                          <FiChevronUp style={{ marginRight: 5 }}></FiChevronUp>
                          {t("general.showLess")}
                        </Fragment>
                      )}
                    </Button>
                  </div>
                </div>
              )}
              <div style={flex.flexFill}></div>


              {(props.readOnly == false && isLargeDevice == true) && (
                <ProductManagerButtons />
              )}


            </div>
          </div>

          {saleItems.length == 0 ? (
            <div
              style={{ marginTop: 20, marginBottom: 20, textAlign: "center" }}
            >
              <span style={{ color: "gray" }}>{t("input.validations.noProducts")}</span>
            </div>
          ) : (
            <Fragment>

              {props.booking_products_enabled == true && (
                <Fragment>
                  <div style={{
                    marginTop: 20,
                    marginBottom: 20,
                    textAlign: "left",
                    backgroundColor: "#eee",
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingLeft: 10,
                  }}>
                    <span>{t("booking.labels.products")}</span>
                  </div>

                  <div
                    style={{
                      border: "solid 1px #eee",
                      borderRadius: 10,
                    }}
                  >
                    {productsTable}
                  </div>
                </Fragment>
              )}
            </Fragment>
          )}

          {payments.length > 0 && props.booking_products_enabled == true && (
            <Fragment>
              <div style={{
                marginTop: 20,
                marginBottom: 20,
                textAlign: "left",
                backgroundColor: "#eee",
                paddingTop: 10,
                paddingBottom: 10,
                paddingLeft: 10,
              }}>
                <span>{t("payments.payments")}</span>
              </div>

              <div
                style={{
                  border: "solid 1px #eee",
                  borderTop: "none",
                  borderRadius: 10,
                }}
              >
                {paymentsTable}
              </div>
            </Fragment>
          )}

          {props.booking_products_enabled == true && summary}


        </NotchedCard>
      </Grid>


    </Fragment>
  );
};

export default BookingProducts;
